import React, { useCallback, useEffect, useState } from "react";
import { Users, UserPlus, Crown, MoreHorizontal, XCircle, UserMinus, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import { avatarFor } from "@/lib/utils-tg";
import InviteUserDialog from "@/components/InviteUserDialog";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export default function TeamPage() {
  const { active, activeId } = useCompany();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  // Permissions for this page:
  //  - canInvite + canRemove: admin/founder OR global admin
  //  - canChangeRole: admin only (matches backend strict rule)
  const isAdmin = active?.role === "admin" || user?.role === "admin";
  const canChangeRole = isAdmin; // strictly admin
  const isAllMode = activeId === "__all__";

  const load = useCallback(async () => {
    if (!active?.id) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/companies/${active.id}/members`);
      setMembers(r.data || []);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [active?.id]);

  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    const isPending = m.status !== "active";
    const display = m.name || m.work_email;
    const ok = await confirm({
      title: isPending ? "Cancel invitation?" : `Remove ${display} from ${active?.name}?`,
      description: isPending
        ? `The pending invite for ${m.work_email} will be revoked. They won't be able to join via the existing link.`
        : `${display} will lose access to ${active?.name}. Their other companies (if any) are not affected.`,
      confirmText: isPending ? "Cancel invite" : "Remove member",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/companies/${active.id}/members/${m.id}`);
      toast.success(isPending ? "Invite cancelled" : "Member removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const changeRole = async (m, newRole) => {
    if (m.role === newRole) return;
    try {
      await api.patch(`/companies/${active.id}/members/${m.id}`, { role: newRole });
      toast.success(`${m.name || m.work_email} is now ${newRole}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change role");
    }
  };

  const setSupervisor = async (m, supId) => {
    try {
      await api.patch(`/companies/${active.id}/members/${m.id}`, { supervisor_id: supId || "" });
      const sup = members.find((x) => x.user_id === supId);
      toast.success(supId ? `${sup?.name || "Supervisor"} now approves ${m.name || m.work_email}'s timesheets` : "Supervisor cleared");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't set supervisor");
    }
  };

  const activeMembers = members.filter((m) => m.status === "active");
  const pending = members.filter((m) => m.status !== "active");

  return (
    <div className="px-6 md:px-10 pt-6 max-w-5xl mx-auto" data-testid="team-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 flex items-center gap-2">
            <Users className="h-7 w-7 text-emerald-600" /> Team
          </h1>
          <p className="text-slate-500 mt-1">
            {isAllMode
              ? "Switch to a specific company to see its team."
              : active?.name
                ? <>People in <span className="font-semibold text-slate-700">{active.name}</span>.</>
                : "Loading…"}
          </p>
        </div>
        {isAdmin && active?.id && !isAllMode && (
          <button
            onClick={() => setInviteOpen(true)}
            className="tg-btn-primary"
            data-testid="team-invite-btn"
          >
            <UserPlus className="h-4 w-4" /> Invite user
          </button>
        )}
      </div>

      {isAllMode ? (
        <div className="tg-card p-8 text-center text-slate-400 text-sm">
          Pick a company from the top-left switcher to manage its team.
        </div>
      ) : loading ? (
        <div className="tg-card p-8 text-center text-slate-400 text-sm">Loading…</div>
      ) : (
        <>
          <Section title={`Active · ${activeMembers.length}`} testid="team-active">
            {activeMembers.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">No active members yet.</div>
            ) : (
              activeMembers.map((m) => (
                <MemberRow key={m.id} m={m} meId={user?.id} canManage={isAdmin} canChangeRole={canChangeRole} onRemove={remove} onChangeRole={changeRole} onSetSupervisor={setSupervisor} supervisorOptions={activeMembers} />
              ))
            )}
          </Section>

          {pending.length > 0 && (
            <Section title={`Pending invites · ${pending.length}`} testid="team-pending">
              {pending.map((m) => (
                <MemberRow key={m.id} m={m} meId={user?.id} canManage={isAdmin} canChangeRole={canChangeRole} onRemove={remove} onChangeRole={changeRole} pending />
              ))}
            </Section>
          )}
        </>
      )}

      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        companyName={active?.name || ""}
        onInvited={load}
      />
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div className="mb-6" data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</div>
      <div className="tg-card divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function MemberRow({ m, meId, pending = false, canManage = false, canChangeRole = false, onRemove, onChangeRole, onSetSupervisor, supervisorOptions = [] }) {
  const display = m.name || m.work_email || "(unknown)";
  const isMe = m.user_id === meId;
  const src = m.avatar_url || avatarFor(display);
  const ROLES = ["employee", "supervisor", "advisor", "founder", "admin"];
  // Candidate supervisors: other registered, active members.
  const supCandidates = supervisorOptions.filter((o) => o.user_id && o.user_id !== m.user_id);
  const currentSup = supervisorOptions.find((o) => o.user_id === m.supervisor_id);
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={`team-member-${m.id}`}>
      <img
        src={src}
        alt={display}
        className="h-10 w-10 rounded-full object-cover bg-slate-100"
        onError={(e) => { e.currentTarget.src = avatarFor(display); }}
      />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800 truncate flex items-center gap-2">
          {display}
          {isMe && <span className="text-[10px] text-slate-400 font-normal">(you)</span>}
          {m.role === "admin" && <Crown className="h-3.5 w-3.5 text-amber-500" />}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {m.work_email} · <span className="capitalize">{m.role}</span>
          {currentSup && <> · <span className="text-slate-400">reports to {currentSup.name || currentSup.work_email}</span></>}
        </div>
      </div>
      <span
        className={`tg-pill text-[10px] ${
          pending
            ? "bg-amber-50 text-amber-700 border-amber-200"
            : "bg-emerald-50 text-emerald-700 border-emerald-200"
        }`}
      >
        {pending ? "pending" : "active"}
      </span>
      {canManage && !isMe && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid={`member-menu-${m.id}`}
              aria-label="More actions"
            >
              <MoreHorizontal className="h-4 w-4 text-slate-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {canChangeRole && (
              <>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer" data-testid={`change-role-${m.id}`}>
                    <Shield className="h-4 w-4 mr-2 text-slate-500" />
                    Change role
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-40">
                    {ROLES.map((r) => (
                      <DropdownMenuItem
                        key={r}
                        onClick={() => onChangeRole?.(m, r)}
                        className={`cursor-pointer capitalize ${r === m.role ? "font-semibold text-emerald-700" : ""}`}
                        data-testid={`set-role-${r}-${m.id}`}
                      >
                        {r === "admin" && <Crown className="h-3.5 w-3.5 mr-2 text-amber-500" />}
                        {r}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer" data-testid={`change-supervisor-${m.id}`}>
                    <Users className="h-4 w-4 mr-2 text-slate-500" />
                    Set supervisor
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-52">
                    <DropdownMenuItem
                      onClick={() => onSetSupervisor?.(m, null)}
                      className={`cursor-pointer ${!m.supervisor_id ? "font-semibold text-emerald-700" : ""}`}
                      data-testid={`set-supervisor-none-${m.id}`}
                    >
                      No supervisor
                    </DropdownMenuItem>
                    {supCandidates.length === 0 ? (
                      <DropdownMenuItem disabled className="text-slate-400">No other members</DropdownMenuItem>
                    ) : (
                      supCandidates.map((o) => (
                        <DropdownMenuItem
                          key={o.user_id}
                          onClick={() => onSetSupervisor?.(m, o.user_id)}
                          className={`cursor-pointer ${o.user_id === m.supervisor_id ? "font-semibold text-emerald-700" : ""}`}
                          data-testid={`set-supervisor-${o.user_id}-${m.id}`}
                        >
                          {o.name || o.work_email}
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onClick={() => onRemove?.(m)}
              className="text-rose-600 focus:text-rose-700 focus:bg-rose-50 cursor-pointer"
              data-testid={pending ? `cancel-invite-${m.id}` : `remove-member-${m.id}`}
            >
              {pending ? <XCircle className="h-4 w-4 mr-2" /> : <UserMinus className="h-4 w-4 mr-2" />}
              {pending ? "Cancel invite" : "Remove from team"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
